// Professional styled .xlsx exporter (RTL, banded rows, frozen header, print-ready).
// Accepts the same `excelData` shape used across the app:
//   { name, items, json_fields, footer }

const NAVY = "FF14304F";
const BLUE = "FF1F5F99";
const BAND = "FFF1F6FB";
const BORDER = "FFC9D3DF";
const GOLD_BG = "FFFFF6DC";
const GREEN = "FF1B7F3B";
const RED = "FFC62828";

const getByPath = (obj, path) =>
  String(path)
    .split(".")
    .reduce((o, k) => (o === null || o === undefined ? o : o[k]), obj);

const resolveValue = (item, spec) => {
  let value;
  if (typeof spec === "string") {
    value = getByPath(item, spec);
  } else if (spec && typeof spec === "object") {
    value = getByPath(item, spec.field);
    if (typeof spec.callback === "function") value = spec.callback(value);
  } else if (typeof spec === "function") {
    value = spec(item);
  }
  return value === null || value === undefined ? "" : value;
};

const NUM_RE = /^-?[\d,]+(\.\d+)?$/;
const toCell = (val) => {
  if (typeof val === "number") return { v: val, numFmt: Number.isInteger(val) ? "#,##0" : "#,##0.00" };
  const s = String(val).trim();
  if (NUM_RE.test(s) && s.length < 16 && !/^0\d/.test(s)) {
    const n = Number(s.replace(/,/g, ""));
    if (!isNaN(n)) return { v: n, numFmt: s.includes(".") ? "#,##0.00" : "#,##0" };
  }
  return { v: String(val) };
};

const thin = { style: "thin", color: { argb: BORDER } };
const allBorders = { top: thin, left: thin, bottom: thin, right: thin };

export async function exportStyledExcel(excelData) {
  const items = excelData && excelData.items;
  const fields = excelData && excelData.json_fields;
  if (!items || !items.length || !fields) return;

  const ExcelJS = (await import("exceljs/dist/exceljs.min.js")).default;

  const keys = Object.keys(fields);
  const rows = items.map((item) => keys.map((k) => toCell(resolveValue(item, fields[k]))));
  const title = excelData.name || "Report";
  const lastCol = keys.length;

  const wb = new ExcelJS.Workbook();
  wb.creator = "Golden Eyes";
  wb.created = new Date();
  const ws = wb.addWorksheet(title.slice(0, 30), {
    views: [{ rightToLeft: true, state: "frozen", ySplit: 4, showGridLines: false }],
    pageSetup: {
      orientation: "landscape",
      paperSize: 9,
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.3, right: 0.3, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 },
    },
    headerFooter: {
      oddFooter: "&Cصفحة &P من &N",
    },
  });

  // column widths from content
  ws.columns = keys.map((k, i) => {
    let max = String(k).length + 4;
    rows.forEach((r) => {
      const len = String(r[i].v).length + (r[i].numFmt ? 3 : 0);
      if (len > max) max = len;
    });
    return { width: Math.min(Math.max(max + 2, 10), 50) };
  });

  // --- Row 1: Title banner
  ws.mergeCells(1, 1, 1, lastCol);
  const t = ws.getCell(1, 1);
  t.value = title;
  t.font = { name: "Tahoma", size: 20, bold: true, color: { argb: "FFFFFFFF" } };
  t.alignment = { horizontal: "center", vertical: "middle" };
  t.fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
  ws.getRow(1).height = 42;

  // --- Row 2: sub info (print date / count)
  ws.mergeCells(2, 1, 2, lastCol);
  const info = ws.getCell(2, 1);
  info.value = `Golden Eyes   |   تاريخ التصدير: ${new Date().toISOString().slice(0, 10)}   |   عدد السجلات: ${rows.length}`;
  info.font = { name: "Tahoma", size: 10, color: { argb: "FF55697F" } };
  info.alignment = { horizontal: "center", vertical: "middle" };
  info.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE4ECF5" } };
  ws.getRow(2).height = 22;

  // --- Row 3: spacer
  ws.getRow(3).height = 6;

  // --- Row 4: column headers
  const headRow = ws.getRow(4);
  keys.forEach((k, i) => {
    const c = headRow.getCell(i + 1);
    c.value = k;
    c.font = { name: "Tahoma", size: 11, bold: true, color: { argb: "FFFFFFFF" } };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BLUE } };
    c.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    c.border = { ...allBorders, bottom: { style: "medium", color: { argb: NAVY } } };
  });
  headRow.height = 30;

  // --- Data rows
  rows.forEach((row, ri) => {
    const r = ws.getRow(5 + ri);
    let longText = false;
    row.forEach((cell, ci) => {
      const c = r.getCell(ci + 1);
      c.value = cell.v;
      if (cell.numFmt) c.numFmt = cell.numFmt;
      const isText = !cell.numFmt;
      const long = isText && String(cell.v).length > 28;
      if (long) longText = true;
      let color = "FF22313F";
      let bold = false;
      if (keys[ci] === "دائن" && cell.numFmt) { color = GREEN; bold = true; }
      else if (keys[ci] === "مدين" && cell.numFmt) { color = RED; bold = true; }
      else if (cell.numFmt && cell.v < 0) color = RED;
      if (cell.numFmt && !bold && ci === 0) bold = true;
      c.font = { name: "Tahoma", size: 10, color: { argb: color }, bold };
      c.alignment = {
        horizontal: long ? "right" : "center",
        vertical: "middle",
        wrapText: true,
        readingOrder: 2,
      };
      c.border = allBorders;
      if (ri % 2 === 1) c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BAND } };
    });
    r.height = longText ? Math.min(15 * Math.ceil(Math.max(...row.map((c) => String(c.v).length)) / 45), 150) + 6 : 22;
  });

  // --- Footer summary rows
  let next = 5 + rows.length + 1;
  if (excelData.footer) {
    const footer = Array.isArray(excelData.footer) ? excelData.footer : [excelData.footer];
    footer.forEach((txt, i) => {
      ws.mergeCells(next, 1, next, lastCol);
      const c = ws.getCell(next, 1);
      c.value = String(txt);
      const last = i === footer.length - 1;
      c.font = { name: "Tahoma", size: last ? 13 : 12, bold: true, color: { argb: last ? "FFFFFFFF" : NAVY } };
      c.alignment = { horizontal: "right", vertical: "middle", indent: 1, readingOrder: 2 };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: last ? NAVY : GOLD_BG } };
      c.border = allBorders;
      ws.getRow(next).height = 28;
      next++;
    });
  }

  ws.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4, column: lastCol } };
  ws.pageSetup.printTitlesRow = "4:4";

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = title + ".xlsx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
